import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Clock,
  DollarSign,
  File,
  SquareCheckBig,
  type LucideIcon,
} from 'lucide-react'
import { CurrencyNumberInput } from '@/components/ui/currency-input'
import { FORM_FIELD_CLASS } from '@/components/ui/form-control-styles'
import { Label } from '@/components/ui/label'
import { cn } from '@/lib/utils'
import type { InterventionType } from '@/types'

type Option = {
  value: InterventionType
  labelKey: string
  descriptionKey: string
  icon: LucideIcon
  iconClassName: string
  iconBackgroundClassName: string
  selectedIconBackgroundClassName: string
  selectedIconClassName: string
}

const OPTIONS: Option[] = [
  {
    value: 'visit_quote',
    labelKey: 'form.interventionTypes.visitQuote.label',
    descriptionKey: 'form.interventionTypes.visitQuote.description',
    icon: File,
    iconClassName: 'text-[#2e5ce6]',
    iconBackgroundClassName: 'bg-[#ebf1fe]',
    selectedIconBackgroundClassName: 'bg-[#2e5ce6]',
    selectedIconClassName: 'text-white',
  },
  {
    value: 'one_time_treatment',
    labelKey: 'form.interventionTypes.oneTimeTreatment.label',
    descriptionKey: 'form.interventionTypes.oneTimeTreatment.description',
    icon: DollarSign,
    iconClassName: 'text-[#d732a8]',
    iconBackgroundClassName: 'bg-[#fcf1fd]',
    selectedIconBackgroundClassName: 'bg-[#d732a8]',
    selectedIconClassName: 'text-[#fcf1fd]',
  },
  {
    value: 'under_contract',
    labelKey: 'form.interventionTypes.underContract.label',
    descriptionKey: 'form.interventionTypes.underContract.description',
    icon: SquareCheckBig,
    iconClassName: 'text-[#ce7e0a]',
    iconBackgroundClassName: 'bg-[#fbf3d9]',
    selectedIconBackgroundClassName: 'bg-[#efac00]',
    selectedIconClassName: 'text-white',
  },
  {
    value: 'warranty_return',
    labelKey: 'form.interventionTypes.warrantyReturn.label',
    descriptionKey: 'form.interventionTypes.warrantyReturn.description',
    icon: Clock,
    iconClassName: 'text-[#518704]',
    iconBackgroundClassName: 'bg-[#f1f7e3]',
    selectedIconBackgroundClassName: 'bg-[#518704]',
    selectedIconClassName: 'text-white',
  },
]

interface Props {
  value: InterventionType | null
  onChange: (value: InterventionType) => void
  amount: number | undefined
  onAmountChange: (amount: number | undefined) => void
  amountError?: string
  contractField?: ReactNode
  warrantyFields?: ReactNode
}

function Selection({ selected }: { selected: boolean }) {
  return (
    <span
      className={cn(
        'flex size-4 shrink-0 items-center justify-center rounded-full border bg-white',
        selected ? 'border-[#10b981] bg-[#10b981]' : 'border-[#b0b5c3]'
      )}
    >
      {selected && <span className='size-2 rounded-full bg-white' />}
    </span>
  )
}

function InfoBanner({
  badge,
  children,
}: {
  badge: string
  children: ReactNode
}) {
  return (
    <div className='flex w-full items-center gap-2 rounded-2xl bg-[#f4f5f8] p-4'>
      <span className='shrink-0 rounded-lg bg-[#fbfbfc] px-2 py-1 text-xs font-normal leading-4 text-[#171a20]'>
        {badge}
      </span>
      <span className='text-xs font-normal leading-4 text-[#3d434e]'>
        {children}
      </span>
    </div>
  )
}

export function InterventionTypeSection({
  value,
  onChange,
  amount,
  onAmountChange,
  amountError,
  contractField,
  warrantyFields,
}: Props) {
  const { t } = useTranslation('jobs')

  return (
    <div className='space-y-3'>
      <div className='grid gap-3 md:grid-cols-2'>
        {OPTIONS.map(option => {
          const selected = value === option.value
          const Icon = option.icon
          return (
            <button
              key={option.value}
              type='button'
              aria-pressed={selected}
              onClick={() => onChange(option.value)}
              className={cn(
                'flex h-[72px] items-center gap-3 rounded-xl border p-4 text-left shadow-[0_1px_4px_rgba(38,45,66,0.05)] transition-colors',
                selected
                  ? 'border-[#10b981] bg-[#ecfdf5]'
                  : 'border-[#e7e9ef] bg-white hover:bg-[#fbfbfc]'
              )}
            >
              <span
                className={cn(
                  'flex size-8 shrink-0 items-center justify-center rounded-lg',
                  selected
                    ? option.selectedIconBackgroundClassName
                    : option.iconBackgroundClassName
                )}
              >
                <Icon
                  className={cn(
                    'size-4',
                    selected
                      ? option.selectedIconClassName
                      : option.iconClassName
                  )}
                  strokeWidth={2}
                />
              </span>
              <span className='min-w-0 flex-1'>
                <span className='block text-sm font-medium leading-[1.4] text-[#171a20]'>
                  {t(option.labelKey)}
                </span>
                <span className='mt-1 block text-xs font-normal leading-4 text-[#3d434e]'>
                  {t(option.descriptionKey)}
                </span>
              </span>
              <Selection selected={selected} />
            </button>
          )
        })}
      </div>

      {value === 'visit_quote' && (
        <InfoBanner badge={t('form.interventionTypes.visitQuote.badge')}>
          {t('form.interventionTypes.visitQuote.hint')}
        </InfoBanner>
      )}

      {value === 'one_time_treatment' && (
        <div className='space-y-1'>
          <Label htmlFor='amount_to_collect' className='text-xs font-medium'>
            {t('form.collectAmount')}
          </Label>
          <CurrencyNumberInput
            id='amount_to_collect'
            value={amount}
            emptyAsZero={false}
            onValueChange={onAmountChange}
            placeholder='0.00'
            className={cn(
              FORM_FIELD_CLASS,
              amountError && 'border border-destructive'
            )}
          />
          {amountError && (
            <p className='text-xs text-destructive'>{amountError}</p>
          )}
        </div>
      )}

      {value === 'under_contract' && (
        <>
          <InfoBanner badge={t('form.interventionTypes.underContract.badge')}>
            {t('form.interventionTypes.underContract.hint')}
          </InfoBanner>
          <div className='space-y-1'>
            <Label
              htmlFor='contract_amount_to_collect'
              className='text-xs font-medium'
            >
              {t('form.collectAmount')}
            </Label>
            <CurrencyNumberInput
              id='contract_amount_to_collect'
              value={amount}
              emptyAsZero={false}
              onValueChange={onAmountChange}
              placeholder='0.00'
              className={cn(
                FORM_FIELD_CLASS,
                amountError && 'border border-destructive'
              )}
            />
            <p className='text-xs font-normal leading-4 text-[#6c7285]'>
              {t('form.interventionTypes.underContract.settledHint')}
            </p>
            {amountError && (
              <p className='text-xs text-destructive'>{amountError}</p>
            )}
          </div>
          {contractField}
        </>
      )}

      {value === 'warranty_return' && (
        <>
          <InfoBanner badge={t('form.interventionTypes.warrantyReturn.badge')}>
            {t('form.interventionTypes.warrantyReturn.hint')}
          </InfoBanner>
          <div className='space-y-1'>
            <Label
              htmlFor='warranty_amount_to_collect'
              className='text-xs font-medium'
            >
              {t('form.collectAmount')}
            </Label>
            <CurrencyNumberInput
              id='warranty_amount_to_collect'
              value={amount}
              emptyAsZero={false}
              onValueChange={onAmountChange}
              placeholder='0.00'
              className={cn(
                FORM_FIELD_CLASS,
                amountError && 'border border-destructive'
              )}
            />
            <p className='text-xs font-normal leading-4 text-[#6c7285]'>
              {t('form.interventionTypes.warrantyReturn.settledHint')}
            </p>
            {amountError && (
              <p className='text-xs text-destructive'>{amountError}</p>
            )}
          </div>
          {warrantyFields}
        </>
      )}
    </div>
  )
}
